//libs
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import size from 'lodash/size';

import { useTranslation } from '@lyearn/i18n';
import { ImageDragDrop } from '@lyearn/icons';
import { UppyFileMeta, useUppy } from '@lyearn/shared/hooks/useUppy';
import { cn } from '@lyearn/style';
//components
import { Box, Button, Typography } from '@lyearn/ui';
import DragDropPlaceholder from '@/features/media/components/DragDropPlaceholder';
import DragDrop from '@/features/media/plugins/DragDrop';
//types
import { ResourceResponseTypeEnum } from '@/types/schema';

export type UploadBoxRef = { addFile?: () => void };

const UploadBoxPlaceholder: React.FC<{
  type: ResourceResponseTypeEnum;
  showFileSelector: () => void;
  error: boolean;
}> = ({ type, showFileSelector, error }) => {
  const { t } = useTranslation('Assignment');

  return (
    <Box
      className={cn(
        'flex flex-1 flex-col items-center justify-center rounded-8 border border-dashed pb-48 pt-64 xs:border-0',
        error ? 'border-border-error-dark' : 'border-border-dark',
      )}>
      <ImageDragDrop fillColorToken="icon-white" />
      <Typography
        className="w-full text-center xs:hidden"
        color="text-text-placeholder"
        variant="heading-s">
        {t('Assignment:Drag&Drop', { type: type.toLowerCase() })}
      </Typography>
      <Button variant="ghost" onClick={showFileSelector}>
        {t('Assignment:SelectFromDevice')}
      </Button>
    </Box>
  );
};

const UploadBox = forwardRef<
  UploadBoxRef,
  {
    type: ResourceResponseTypeEnum;
    uppy: ReturnType<typeof useUppy>['uppy'];
    files?: ReturnType<typeof useUppy>['files'];
    children?: React.ReactNode;
    accept?: string;
    multiple?: boolean | null;
    renderer?: (showFileSelector: () => void, error?: boolean) => JSX.Element;
    fileMeta?: Pick<UppyFileMeta, 'entityId'>;
    className?: string;
  }
>(({ type, multiple, children, uppy, files, accept, renderer, fileMeta, className }, ref) => {
  const fileInput = useRef<DragDrop>(null);
  const [error, setError] = useState<boolean>(false);
  const showFileSelector = React.useCallback(() => fileInput.current?.showUploadDialog(), []);
  useImperativeHandle(ref, () => ({ addFile: fileInput.current?.showUploadDialog }), [fileInput]);

  const DefaultRenderer = () =>
    size(files) === 0 ? (
      <UploadBoxPlaceholder error={error} showFileSelector={showFileSelector} type={type} />
    ) : (
      children
    );

  return (
    <DragDrop
      ref={fileInput}
      DragView={<DragDropPlaceholder />}
      accept={accept}
      className={cn('flex flex-1 flex-shrink-0', className)}
      fileMeta={fileMeta}
      multiple={multiple}
      uppy={uppy}
      onError={setError}>
      {renderer ? renderer(showFileSelector, error) : DefaultRenderer()}
    </DragDrop>
  );
});
export default UploadBox;
